import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Button } from '../common';
import './RichTextEditor.css';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  onSave: () => void;
  onCancel: () => void;
  placeholder?: string;
}

/**
 * Professional WYSIWYG editor using Tiptap
 * Provides rich text editing with formatting toolbar
 */
export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  onSave,
  onCancel,
  placeholder = 'Add a more detailed description...'
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'rich-text-editor__content',
        'data-placeholder': placeholder,
      },
    },
  });

  if (!editor) {
    return null;
  }

  return (
    <div className="rich-text-editor">
      <div className="rich-text-editor__toolbar">
        <div className="rich-text-editor__toolbar-group">
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`rich-text-editor__btn ${editor.isActive('bold') ? 'is-active' : ''}`}
            title="Bold (Ctrl+B)"
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`rich-text-editor__btn ${editor.isActive('italic') ? 'is-active' : ''}`}
            title="Italic (Ctrl+I)"
          >
            <em>I</em>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={`rich-text-editor__btn ${editor.isActive('strike') ? 'is-active' : ''}`}
            title="Strikethrough"
          >
            <s>S</s>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleCode().run()}
            className={`rich-text-editor__btn ${editor.isActive('code') ? 'is-active' : ''}`}
            title="Code"
          >
            {'</>'}
          </button>
        </div>

        <div className="rich-text-editor__toolbar-group">
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={`rich-text-editor__btn ${editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}`}
            title="Heading 1"
          >
            H1
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={`rich-text-editor__btn ${editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}`}
            title="Heading 2"
          >
            H2
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={`rich-text-editor__btn ${editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}`}
            title="Heading 3"
          >
            H3
          </button>
        </div>

        <div className="rich-text-editor__toolbar-group">
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`rich-text-editor__btn ${editor.isActive('bulletList') ? 'is-active' : ''}`}
            title="Bullet List"
          >
            •
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`rich-text-editor__btn ${editor.isActive('orderedList') ? 'is-active' : ''}`}
            title="Numbered List"
          >
            1.
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            className={`rich-text-editor__btn ${editor.isActive('codeBlock') ? 'is-active' : ''}`}
            title="Code Block"
          >
            {'{ }'}
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={`rich-text-editor__btn ${editor.isActive('blockquote') ? 'is-active' : ''}`}
            title="Quote"
          >
            "
          </button>
        </div>

        <div className="rich-text-editor__toolbar-group">
          <button
            type="button"
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            className="rich-text-editor__btn"
            title="Horizontal Rule"
          >
            ―
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setHardBreak().run()}
            className="rich-text-editor__btn"
            title="Line Break"
          >
            ↵
          </button>
        </div>

        <div className="rich-text-editor__toolbar-group">
          <button
            type="button"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            className="rich-text-editor__btn"
            title="Undo (Ctrl+Z)"
          >
            ↶
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            className="rich-text-editor__btn"
            title="Redo (Ctrl+Y)"
          >
            ↷
          </button>
        </div>
      </div>

      <EditorContent editor={editor} />

      <div className="rich-text-editor__actions">
        <Button variant="primary" size="sm" onClick={onSave}>
          Save
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      <div className="rich-text-editor__help">
        <details>
          <summary>⌨️ Keyboard shortcuts</summary>
          <div className="rich-text-editor__help-content">
            <div className="rich-text-editor__help-row">
              <kbd>Ctrl</kbd> + <kbd>B</kbd>
              <span>→</span>
              <span>Bold</span>
            </div>
            <div className="rich-text-editor__help-row">
              <kbd>Ctrl</kbd> + <kbd>I</kbd>
              <span>→</span>
              <span>Italic</span>
            </div>
            <div className="rich-text-editor__help-row">
              <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>X</kbd>
              <span>→</span>
              <span>Strikethrough</span>
            </div>
            <div className="rich-text-editor__help-row">
              <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>C</kbd>
              <span>→</span>
              <span>Code</span>
            </div>
            <div className="rich-text-editor__help-row">
              <kbd>Ctrl</kbd> + <kbd>Alt</kbd> + <kbd>1-3</kbd>
              <span>→</span>
              <span>Headings</span>
            </div>
            <div className="rich-text-editor__help-row">
              <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>8</kbd>
              <span>→</span>
              <span>Bullet list</span>
            </div>
            <div className="rich-text-editor__help-row">
              <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>7</kbd>
              <span>→</span>
              <span>Numbered list</span>
            </div>
            <div className="rich-text-editor__help-row">
              <kbd>Ctrl</kbd> + <kbd>Z</kbd> / <kbd>Ctrl</kbd> + <kbd>Y</kbd>
              <span>→</span>
              <span>Undo / Redo</span>
            </div>
          </div>
        </details>
      </div>
    </div>
  );
};
